import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import {
  activateLendingPad,
  addLendingPadCompany,
  clearLendingPadRoleCapture,
  disableLendingPad,
  getLendingPadOrganization,
  getLendingPadStats,
  getLendingPadSupportRequest,
  explainLendingPadAccess,
  listLendingPadActivity,
  listLendingPadPeople,
  listLendingPadSyncHistory,
  reconcileLendingPad,
  retryLendingPadActivation,
  retryLendingPadBackfill,
  saveLendingPadConfig,
  saveLendingPadImportScope,
  saveLendingPadSyncStages,
  saveLendingPadIgnoredFolders,
  addLendingPadSelectedLoan,
  removeLendingPadSelectedLoan,
  saveLendingPadTeamEmails,
  setLendingPadRoleSelection,
  linkLendingPadLosEmail,
  unlinkLendingPadLosEmail,
  testLendingPadConnection,
  updateLendingPadCompany,
} from "../api";
import {
  describeSyncReason,
  feedMeta,
  formatDate,
  formatDatePrecise,
  formatRelativeTime,
  originMeta,
  resolvePrimaryAction,
  stateMeta,
} from "../constants";

import { buildLendingPadSupportRequest } from "../supportRequest";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { InfoDot, InfoTip } from "./InfoTip";
import { effectiveAccess, subjectKey } from "../people";
import { LendingPadImportPolicyTab } from "./LendingPadImportPolicyTab";
import { LendingPadPeopleTab } from "./LendingPadPeopleTab";
import { OrgAvatar } from "./OrgAvatar";
import { StatusBadge } from "./StatusBadge";

// Rows per page in the Syncs tab.
const SYNC_PAGE_SIZE = 25;
// How often to re-check an org that is mid-sync or mid-test. Short enough that
// the panel feels live, and it stops as soon as the state settles.
const TRANSIENT_POLL_MS = 10000;

// A plain-language "what to do" line for the states that need operator action.
//
// Note "degraded" is now a *confirmed* behind state — a silent feed no longer
// produces it (that shows as a connected org with a soft "no recent updates"
// flag and its own guidance in the Data feed card). So the degraded advice can
// speak plainly about missing data rather than hedging about whether there is a
// problem at all.
function recommendationFor(state) {
  switch (state) {
    case "failed":
      return "Retry activation to run the checks again. If it keeps failing, copy the support request and send it to LendingPad.";
    case "awaiting_external_activation":
      return "Copy the support request and send it to LendingPad, then retry once they confirm access is enabled.";
    case "degraded":
      return "Data is confirmed behind. Use the Data Check tab to see what is missing, then copy the support request and ask LendingPad to confirm the exports are enabled for this Company ID.";
    default:
      return "";
  }
}

// What to tell the operator after saving the loans-to-import scope. The scope
// itself always applies immediately; only the import of the loans it newly
// allows can be deferred, and that has to be said out loud — a bare "Saved."
// would hide thousands of loans that never arrive.
function describeImportScopeSave({ importScope, backfill, selectedLoansImport }) {
  if (importScope === "selected_loans") {
    const queued = selectedLoansImport?.queued || 0;

    return {
      tone: "success",
      message:
        queued > 0
          ? `Saved. Only the selected LendingPad loans will sync from now on. Importing ${queued} of them that are not in Addy yet…`
          : "Saved. Only the selected LendingPad loans will sync from now on.",
    };
  }

  if (importScope !== "all_loans") {
    return {
      tone: "success",
      message: backfill?.triggered
        ? "Saved. Only loans with a team member in this organization will sync from now on. Importing the ones skipped while only selected loans were imported…"
        : "Saved. Only loans with a team member in this organization will sync from now on.",
    };
  }

  if (backfill?.triggered) {
    return {
      tone: "success",
      message:
        "Saved. Importing every existing loan in the selected stages. This can take a few minutes.",
    };
  }

  if (backfill?.reason === "backfill_in_progress") {
    return {
      tone: "warning",
      message:
        "Saved, but a sync is already running so the remaining loans were not requested. Run Sync Data once it finishes.",
    };
  }

  if (backfill?.reason === "integration_not_active") {
    return {
      tone: "warning",
      message:
        "Saved. Existing loans will be imported when this integration is activated.",
    };
  }

  return { tone: "success", message: "Saved." };
}

// The alias of the company the backend says the detail describes.
function selectedCompanyAlias(organization) {
  const selected = (organization?.companies || []).find(
    (entry) => entry.companyId === organization?.selectedCompanyId,
  );

  return selected?.alias || "";
}

// Names the company a tab acts on. Single-company organizations never see it,
// except while adding their second company.
function CompanyScopeNote({ children }) {
  return (
    <div className="integrations-save-note" style={{ marginTop: 0, marginBottom: 14 }}>
      <span aria-hidden="true">ℹ️</span>
      <span>{children}</span>
    </div>
  );
}

function HealthChecks({ checks = [] }) {
  if (checks.length === 0) {
    return (
      <div className="integrations-empty">No health checks available yet.</div>
    );
  }

  return (
    <div className="integrations-checks">
      {checks.map((check) => (
        <div key={check.id} className="integrations-check">
          <span
            className={`integrations-check-ico ${check.passed ? "" : "bad"}`}
            aria-hidden="true"
          >
            {check.passed ? "✓" : "✕"}
          </span>
          <div>
            <div className="integrations-check-label">{check.label}</div>
            <div className="integrations-check-sub">{check.detail}</div>
          </div>
          <span className="integrations-check-val">
            {check.passed ? "Passed" : "Failed"}
          </span>
        </div>
      ))}
    </div>
  );
}

function ActivityTimeline({ events = [] }) {
  if (events.length === 0) {
    return <div className="integrations-empty">No activity recorded yet.</div>;
  }

  return (
    <div className="integrations-timeline">
      {events.map((event) => {
        const isError = event.result === "failed";
        return (
          <div key={event.id} className="integrations-event">
            <span
              className={`integrations-event-dot ${
                isError ? "error" : "ok"
              }`}
            />
            <div>
              <div className="integrations-event-title">
                {event.action}
                {isError ? (
                  <span className="integrations-mini-badge error">Failed</span>
                ) : null}
              </div>
              <div className="integrations-event-meta">
                {formatDate(Date.parse(event.createdAt))}
                {event.actorId ? ` · ${event.actorId}` : ""}
                {event.durationMs ? ` · ${event.durationMs}ms` : ""}
              </div>
              {event.error?.message ? (
                <div className="integrations-event-desc">
                  {event.error.message}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DetailPanel({
  env,
  organizationId,
  companyId,
  onSelectCompany,
  onUpdated,
  onLoaded,
  onClose,
}) {
  const [detail, setDetail] = useState(null);
  const [activity, setActivity] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [company, setCompany] = useState("");
  const [user, setUser] = useState("");
  const [alias, setAlias] = useState("");
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  // Background reloads (polls, window refocus) must not wipe credentials the
  // operator is still typing; a ref because the refocus listener closes over
  // the first render's loadDetail.
  const hasUnsavedCredentialsRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [operation, setOperation] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [syncFilters, setSyncFilters] = useState({ triggeredBy: "", status: "" });
  const [syncCursor, setSyncCursor] = useState(null);
  const [loadingMoreSyncs, setLoadingMoreSyncs] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconcileError, setReconcileError] = useState(null);
  const [people, setPeople] = useState(null);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState(null);

  const isBusy = Boolean(operation);
  // Until the detail of this organization arrives, a missing companyId lets the
  // backend fall back to the primary company.
  const loadedCompanyId =
    detail?.id === organizationId ? detail?.selectedCompanyId : null;
  const activeCompanyId = companyId || loadedCompanyId || null;
  const companyScope = activeCompanyId ? { companyId: activeCompanyId } : {};

  const resetConnectionFields = (organization) => {
    setCompany(organization?.draftConfig?.company || organization?.company || "");
    setUser(
      organization?.draftConfig?.user || organization?.systemAdminUserId || "",
    );
    setAlias(selectedCompanyAlias(organization));
  };

  // `silent` keeps the panel from flashing its skeleton on background polls and
  // window refocus — the data updates underneath without the view jumping.
  const loadDetail = async ({ silent = false, filters = syncFilters } = {}) => {
    if (!organizationId) {
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    setLoadError(null);
    try {
      const [detailJson, activityJson, syncJson] = await Promise.all([
        getLendingPadOrganization(env, organizationId, companyId),
        listLendingPadActivity(env, organizationId, { limit: 20, companyId }),
        listLendingPadSyncHistory(env, organizationId, {
          limit: SYNC_PAGE_SIZE,
          companyId,
          ...(filters.triggeredBy ? { triggeredBy: filters.triggeredBy } : {}),
          ...(filters.status ? { status: filters.status } : {}),
        }),
      ]);

      if (!detailJson?.success) {
        throw new Error(detailJson?.reason || "Failed to load organization");
      }

      const organization = detailJson.organization;
      setDetail(organization);
      // A background poll must not wipe a company being added; any other
      // reload shows the saved company again, unless edits are still unsaved.
      if (!hasUnsavedCredentialsRef.current && (!silent || !isAddingCompany)) {
        resetConnectionFields(organization);
      }
      if (!silent) {
        setIsAddingCompany(false);
      }
      setActivity(activityJson?.events || []);
      setSyncHistory(syncJson?.entries || []);
      setSyncCursor(syncJson?.nextCursor || null);
      setLoadedAt(Date.now());
      if (onLoaded) {
        onLoaded(organization);
      }
    } catch (error) {
      setLoadError(error?.message || "Failed to load organization detail");
    } finally {
      setLoading(false);
    }
  };

  // Totals are fetched separately: they are aggregation queries, and a slow or
  // failing count must never stop the panel from rendering.
  const loadStats = async () => {
    if (!organizationId) {
      return;
    }

    try {
      const json = await getLendingPadStats(env, organizationId, activeCompanyId);
      setStats(json?.success ? json.stats : null);
    } catch (_error) {
      setStats(null);
    }
  };

  // Loaded only when the People tab is first opened. It scans
  // organization-members and counts loans per person, so putting it in
  // loadDetail would add that to every panel open — and a failure there would
  // break the whole panel rather than one tab.
  const loadPeople = async () => {
    if (!organizationId) {
      return;
    }

    setPeopleLoading(true);
    setPeopleError(null);
    try {
      const json = await listLendingPadPeople(env, organizationId, activeCompanyId);
      if (!json?.success) {
        throw new Error(json?.reason || "Could not load this organization's people");
      }

      setPeople(json.data);
    } catch (error) {
      setPeople(null);
      setPeopleError(error?.message || "Could not load this organization's people");
    } finally {
      setPeopleLoading(false);
    }
  };


  useEffect(() => {
    hasUnsavedCredentialsRef.current = false;
    setActiveTab("overview");
    setIsAddingCompany(false);
  }, [env, organizationId]);

  // Switching company keeps the open tab, so ops can compare the same rules
  // across companies.
  useEffect(() => {
    setStats(null);
    setReconciliation(null);
    setReconcileError(null);
    setPeople(null);
    setPeopleError(null);
    setSyncFilters({ triggeredBy: "", status: "" });
    loadDetail({ filters: { triggeredBy: "", status: "" } });
    loadStats();
    if (activeTab === "people") {
      loadPeople();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, organizationId, companyId]);

  useEffect(() => {
    if (activeTab !== "people" || people || peopleLoading || peopleError) {
      return;
    }

    loadPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);


  // Poll only while the org sits in a state that resolves on its own. Without
  // this the spinner for `syncing` / `testing` runs forever: the work finishes
  // server-side and the panel never hears about it.
  const isTransient =
    detail?.effectiveState === "syncing" || detail?.effectiveState === "testing";
  useEffect(() => {
    if (!organizationId || !isTransient) {
      return undefined;
    }

    const timer = setInterval(() => {
      loadDetail({ silent: true });
      loadStats();
    }, TRANSIENT_POLL_MS);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, env, isTransient]);

  // Revalidate when the operator comes back to the tab. Ops leaves this panel
  // open for long stretches, and stale-but-plausible numbers are worse than a
  // brief loading state.
  useEffect(() => {
    if (!organizationId) {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadDetail({ silent: true });
        loadStats();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, env]);

  // Relative labels ("37h ago") are computed at render against Date.now(), so
  // without a tick they freeze at whatever they were when the panel mounted.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setClockTick((value) => value + 1), 60000);

    return () => clearInterval(timer);
  }, []);

  const applySyncFilter = (patch) => {
    const next = { ...syncFilters, ...patch };
    setSyncFilters(next);
    loadDetail({ silent: true, filters: next });
  };

  const loadMoreSyncs = async () => {
    if (!syncCursor) {
      return;
    }

    setLoadingMoreSyncs(true);
    try {
      const json = await listLendingPadSyncHistory(env, organizationId, {
        limit: SYNC_PAGE_SIZE,
        cursor: syncCursor,
        companyId: activeCompanyId,
        ...(syncFilters.triggeredBy
          ? { triggeredBy: syncFilters.triggeredBy }
          : {}),
        ...(syncFilters.status ? { status: syncFilters.status } : {}),
      });
      setSyncHistory((current) => [...current, ...(json?.entries || [])]);
      setSyncCursor(json?.nextCursor || null);
    } catch (error) {
      toast.error(error?.message || "Failed to load more sync history");
    } finally {
      setLoadingMoreSyncs(false);
    }
  };

  const applyRoleSelection = (subject, opsRoleSelection) =>
    runAction(
      `visibility:${subjectKey(subject)}`,
      (body) =>
        setLendingPadRoleSelection(env, {
          ...body,
          subjectType: subject.subjectType,
          memberId: subject.memberId || null,
          opsRoleSelection,
        }),
      "Saved.",
      { after: loadPeople },
    );

  // Only widening needs a confirmation. Tightening someone to their own loans
  // is the safe direction and asking about it just trains people to click
  // through the dialog that matters.
  const handleRoleSelect = (subject, opsRoleSelection) => {
    const widens =
      opsRoleSelection !== null &&
      opsRoleSelection === subject.assignableAdminRole &&
      effectiveAccess(subject) !== "all";

    if (widens) {
      setConfirmAction({
        kind: "visibility",
        subject,
        opsRoleSelection,
        name: subject.name || subject.email || "this person",
      });

      return;
    }

    applyRoleSelection(subject, opsRoleSelection);
  };

  const handleRoleClear = (subject) =>
    setConfirmAction({
      kind: "clear-capture",
      subject,
      name: subject.name || subject.email || "this person",
    });

  // Linking grants access to every loan carrying the address, so it is
  // confirmed like widening is. Unlinking only takes access away and is
  // confirmed too, because the loans vanish from the person's board at once.
  const handleLinkLosEmail = (subject, email) =>
    setConfirmAction({
      kind: "link-los-email",
      subject,
      email,
      name: subject.name || subject.email || "this person",
    });

  const handleUnlinkLosEmail = (subject, email) =>
    setConfirmAction({
      kind: "unlink-los-email",
      subject,
      email,
      name: subject.name || subject.email || "this person",
    });

  const applyLosEmailChange = (subject, email, request, successMessage) =>
    runAction(
      `visibility:${subjectKey(subject)}`,
      (body) =>
        request(env, {
          ...body,
          subjectType: subject.subjectType,
          memberId: subject.memberId || null,
          email,
        }),
      successMessage,
      { after: loadPeople, isPersonLevel: true },
    );

  // Read-only: issues GETs to LendingPad and compares against what we hold.
  // Nothing is imported, so this is safe to run at any time on any org.
  const handleReconcile = async () => {
    setOperation("reconcile");
    setReconcileError(null);
    try {
      const json = await reconcileLendingPad(env, organizationId, activeCompanyId);
      if (!json?.success) {
        throw new Error(json?.reason || "Comparison failed");
      }

      setReconciliation(json.reconciliation);
    } catch (error) {
      setReconciliation(null);
      setReconcileError(error?.message || "Comparison failed");
    } finally {
      setOperation(null);
    }
  };

  // `options.after` runs on success alongside the usual reloads, for tabs that
  // own state loadDetail knows nothing about. `options.isPersonLevel` marks
  // actions on a person, which hold in every company of the organization; a
  // LendingPad role decision is per company and carries the selected one.
  const runAction = async (actionName, request, successMessage, options = {}) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");
      return;
    }

    setOperation(actionName);

    try {
      const json = await request({
        userId: adminUserId,
        organizationId,
        ...(options.isPersonLevel ? {} : companyScope),
      });

      if (!json?.success) {
        const message = json?.reason || json?.message || `${actionName} failed`;
        toast.error(message);
        return;
      }

      toast.success(successMessage || json?.message || `${actionName} completed`);
      await loadDetail();
      loadStats();
      if (options.after) {
        await options.after(json);
      }
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || `${actionName} failed`);
    } finally {
      setOperation(null);
      setConfirmAction(null);
    }
  };

  const handleSave = async () => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");
      return;
    }

    setOperation("save");
    try {
      const trimmedAlias = alias.trim();
      if (activeCompanyId && trimmedAlias !== selectedCompanyAlias(detail)) {
        const aliasJson = await updateLendingPadCompany(
          env,
          organizationId,
          activeCompanyId,
          { userId: adminUserId, alias: trimmedAlias || null },
        );

        if (!aliasJson?.success) {
          toast.error(aliasJson?.reason || "Failed to save the alias");

          return;
        }
      }

      const json = await saveLendingPadConfig(env, {
        userId: adminUserId,
        organizationId,
        ...companyScope,
        company: company.trim(),
        user: user.trim(),
      });

      if (!json?.success) {
        toast.error(json?.reason || "Failed to save configuration");
        return;
      }

      toast.success("Saved. Run Test Connection before activating.");
      hasUnsavedCredentialsRef.current = false;
      if (onUpdated) {
        onUpdated();
      }

      // A corrected Company ID re-keys the link; the old id no longer exists.
      const savedCompanyId = company.trim();
      if (activeCompanyId && savedCompanyId !== activeCompanyId) {
        onSelectCompany(savedCompanyId);

        return;
      }

      await loadDetail();
      loadStats();
    } catch (error) {
      toast.error(error?.message || "Failed to save configuration");
    } finally {
      setOperation(null);
    }
  };

  const startAddingCompany = () => {
    hasUnsavedCredentialsRef.current = false;
    setIsAddingCompany(true);
    setCompany("");
    setUser("");
    setAlias("");
    setActiveTab("connection");
  };

  const stopAddingCompany = () => {
    hasUnsavedCredentialsRef.current = false;
    setIsAddingCompany(false);
    resetConnectionFields(detail);
  };

  const selectTab = (tabId) => {
    if (isAddingCompany) {
      stopAddingCompany();
    }

    setActiveTab(tabId);
  };

  const selectCompany = (nextCompanyId) => {
    if (isAddingCompany) {
      stopAddingCompany();
    }

    // Unsaved edits belong to the company being left, not the next one.
    hasUnsavedCredentialsRef.current = false;
    onSelectCompany(nextCompanyId);
  };

  // The new company starts with the most restrictive import rules; the backend
  // sets them, so only the connection fields are sent.
  const handleAddCompany = async () => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");

      return;
    }

    const newCompanyId = company.trim();
    const trimmedAlias = alias.trim();
    setOperation("save");
    try {
      const json = await addLendingPadCompany(env, organizationId, {
        userId: adminUserId,
        companyId: newCompanyId,
        lendingPadUserId: user.trim(),
        ...(trimmedAlias ? { alias: trimmedAlias } : {}),
      });

      if (!json?.success) {
        toast.error(json?.reason || json?.message || "Failed to add the company");

        return;
      }

      toast.success("Saved. Run Test Connection before activating.");
      hasUnsavedCredentialsRef.current = false;
      setIsAddingCompany(false);
      onSelectCompany(json.company?.companyId || newCompanyId);
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to add the company");
    } finally {
      setOperation(null);
    }
  };

  const handleSaveStages = async (syncedStatusIds) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");
      return;
    }

    setOperation("stages");
    try {
      const json = await saveLendingPadSyncStages(env, {
        userId: adminUserId,
        organizationId,
        ...companyScope,
        syncedStatusIds,
      });

      if (!json?.success) {
        toast.error(json?.reason || json?.message || "Failed to update import stages");
        return;
      }

      const addedCount = Array.isArray(json?.addedStatusIds)
        ? json.addedStatusIds.length
        : 0;
      toast.success(
        addedCount > 0
          ? `Import stages updated. Backfilling existing loans for ${addedCount} newly enabled stage(s)…`
          : "Import stages updated.",
      );
      await loadDetail();
      loadStats();
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update import stages");
    } finally {
      setOperation(null);
    }
  };


  const handleSaveIgnoredFolders = async (ignoredDocumentFolders) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");

      return;
    }

    setOperation("folders");
    try {
      const json = await saveLendingPadIgnoredFolders(env, organizationId, {
        userId: adminUserId,
        ...companyScope,
        ignoredDocumentFolders,
      });

      if (!json?.success) {
        toast.error(json?.reason || json?.message || "Failed to update ignored folders");

        return;
      }

      toast.success("Ignored folders updated. They apply to new documents from the next sync.");
      await loadDetail();
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update ignored folders");
    } finally {
      setOperation(null);
    }
  };

  const handleAddSelectedLoan = async (loanNumber) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");

      return false;
    }

    setOperation("selectedLoans");
    try {
      const json = await addLendingPadSelectedLoan(env, organizationId, {
        userId: adminUserId,
        ...companyScope,
        loanNumber,
      });

      if (!json?.success) {
        toast.error(json?.reason || json?.message || `Failed to select loan ${loanNumber}`);

        return false;
      }

      toast.success(
        json.importQueued
          ? `Loan ${loanNumber} selected. Importing it now.`
          : json.message || `Loan ${loanNumber} selected.`,
      );
      await loadDetail();
      if (onUpdated) {
        onUpdated();
      }

      return true;
    } catch (error) {
      toast.error(error?.message || `Failed to select loan ${loanNumber}`);

      return false;
    } finally {
      setOperation(null);
    }
  };

  const handleRemoveSelectedLoan = async (lendingPadLoanId) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");

      return;
    }

    setOperation("selectedLoans");
    try {
      const json = await removeLendingPadSelectedLoan(
        env,
        organizationId,
        lendingPadLoanId,
        activeCompanyId,
      );

      if (!json?.success) {
        toast.error(json?.reason || json?.message || "Failed to remove the loan");

        return;
      }

      toast.success(json.message || "Loan removed from the selected loans.");
      await loadDetail();
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to remove the loan");
    } finally {
      setOperation(null);
    }
  };

  const applyImportScope = async (importScope) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");

      return;
    }

    setOperation("importScope");
    try {
      const json = await saveLendingPadImportScope(env, {
        userId: adminUserId,
        organizationId,
        ...companyScope,
        importScope,
      });

      if (!json?.success) {
        toast.error(
          json?.reason || json?.message || "Failed to update loans to import",
        );

        return;
      }

      const { tone, message } = describeImportScopeSave(json);
      toast[tone](message);
      await loadDetail();
      loadStats();
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update loans to import");
    } finally {
      setOperation(null);
      setConfirmAction(null);
    }
  };

  // Only widening is confirmed. It makes another team's loans visible to this
  // customer and narrowing back does not undo it — the imported loans stay.
  const handleSaveImportScope = (importScope) => {
    if (importScope === "all_loans") {
      setConfirmAction({ kind: "import-scope", importScope });

      return;
    }

    applyImportScope(importScope);
  };

  // Declared addresses apply one at a time, behind a confirmation, so the
  // People tab has one saving model: what the select does, the list does too.
  const applyDeclaredAddresses = async (email, teamEmails, successMessage) => {
    const adminUserId = getAuth()?.currentUser?.uid;
    if (!adminUserId) {
      toast.error("Admin user not found. Please refresh and sign in again.");

      return;
    }

    setOperation(`declared:${email}`);
    try {
      const json = await saveLendingPadTeamEmails(env, {
        userId: adminUserId,
        organizationId,
        ...companyScope,
        teamEmails,
      });

      if (!json?.success) {
        toast.error(json?.reason || json?.message || "Failed to save");

        return;
      }

      toast.success(
        json?.backfill?.triggered
          ? `${successMessage} Importing their existing loans…`
          : successMessage,
      );
      await Promise.all([loadDetail({ silent: true }), loadPeople()]);
      loadStats();
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save");
    } finally {
      setOperation(null);
      setConfirmAction(null);
    }
  };

  const declaredEmails = () =>
    (people?.declared || []).map((entry) => entry.email);

  const handleAddAddress = (email) =>
    setConfirmAction({ kind: "add-address", email });

  const handleRemoveAddress = (email) =>
    setConfirmAction({ kind: "remove-address", email });

  // Read-only: the same verdict the loan board applies, with the chain behind
  // it. Errors surface inside the inspector, not as a toast.
  const explainAccess = async (params) => {
    const json = await explainLendingPadAccess(env, organizationId, params);
    if (!json?.success) {
      throw new Error(json?.reason || "Could not check access");
    }

    return json.data;
  };

  const handlePrimary = (action) => {
    if (action.key === "sync") {
      setConfirmAction("backfill");
    } else if (action.key === "test") {
      runAction(
        "test",
        (body) => testLendingPadConnection(env, body),
        "Connection successful.",
      );
    } else if (action.key === "activate") {
      setConfirmAction("activate");
    }
  };

  const handleCopySupport = async () => {
    // Prefer the backend-built text (exact copy of the email, with requester
    // name/email resolved). Fall back to the local template if the call fails.
    let text = buildLendingPadSupportRequest(detail);
    try {
      const json = await getLendingPadSupportRequest(
        env,
        organizationId,
        activeCompanyId,
      );
      if (json?.success && json?.text) {
        text = json.text;
      }
    } catch (_error) {
      /* keep local fallback */
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Support request copied to clipboard.");
    } catch (_error) {
      toast.error("Could not copy. Please try again.");
    }
  };

  const lastTestSummary = useMemo(() => {
    const health = detail?.connectionHealth;
    if (!health?.testedAt) {
      return "Not tested yet";
    }
    return formatRelativeTime(Date.parse(health.testedAt));
  }, [detail]);

  if (!organizationId) {
    return (
      <div className="integrations-card integrations-detail-panel">
        <div className="integrations-detail-empty">
          <div className="integrations-detail-empty-ico" aria-hidden="true">
            ◎
          </div>
          <h3>No organization selected</h3>
          <p>
            Search or pick an organization from the list to view its LendingPad
            connection, health checks and history.
          </p>
        </div>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="integrations-card integrations-detail-panel">
        <div className="integrations-skeleton">
          <div className="integrations-sk-row">
            <div className="integrations-sk" style={{ width: 46, height: 46, borderRadius: 11 }} />
            <div style={{ flex: 1 }}>
              <div className="integrations-sk" style={{ height: 16, width: "60%" }} />
              <div className="integrations-sk" style={{ height: 11, width: "40%", marginTop: 8 }} />
            </div>
          </div>
          <div className="integrations-sk" style={{ height: 64, marginBottom: 14 }} />
          <div className="integrations-sk" style={{ height: 38, marginBottom: 14 }} />
          <div className="integrations-sk" style={{ height: 52, marginBottom: 9 }} />
          <div className="integrations-sk" style={{ height: 52 }} />
        </div>
      </div>
    );
  }

  if (loadError && !detail) {
    return (
      <div className="integrations-card integrations-detail-panel">
        <div className="integrations-card-body">
          <div className="integrations-result err">{loadError}</div>
        </div>
      </div>
    );
  }

  const state = detail?.effectiveState;
  const meta = stateMeta(state);
  const primary = resolvePrimaryAction(detail);
  const recommendation = recommendationFor(state);
  // `lastError` actually holds the last health message — which is a SUCCESS
  // string ("LendingPad connection successful") when the test passed. Only show
  // the red tech line for a genuine failure, and only surface the whole block
  // for states that need operator attention.
  const showTech =
    state === "failed" &&
    Boolean(detail?.lastError) &&
    !/successful/i.test(detail.lastError);
  const hasErrorBlock = Boolean(recommendation) || showTech;
  const backfillState = detail?.backfill?.state || detail?.backfillState;
  const isSyncing = state === "syncing";
  const passedChecks = (detail?.readinessChecks || []).filter(
    (check) => check.passed,
  ).length;
  const totalChecks = (detail?.readinessChecks || []).length;
  const feed = feedMeta(detail?.feedStatus);

  // Actions that talk to LendingPad need a saved Company ID first. Until the
  // credentials are persisted, Test / Activate / Disable stay disabled — only
  // Save and Copy Support Request work.
  const savedCompany = (
    detail?.draftConfig?.company ||
    detail?.company ||
    ""
  ).trim();
  const hasSavedCredentials = Boolean(savedCompany);
  const credsTip = "Save the Company ID in the Connection tab first.";
  const companies = detail?.companies || [];
  const selectedCompany = companies.find(
    (entry) => entry.companyId === activeCompanyId,
  );
  const hasManyCompanies = companies.length > 1;
  const canAddCompany = companies.length > 0;
  const companyName = selectedCompany?.displayName || "this company";
  // LendingPad delivers loans under the Company ID, so it is fixed once live.
  const isCompanyIdLocked =
    !isAddingCompany && selectedCompany?.status === "approved";
  const bannerTitle = hasSavedCredentials ? meta.title : "Not set up yet";
  const bannerDesc = hasSavedCredentials
    ? meta.desc
    : "Add the Company ID and Admin User ID in the Connection tab to set up this integration.";

  return (
    <div className="integrations-card integrations-detail-panel">
      <div className="integrations-detail-head">
        <div className="integrations-detail-id-row">
          <div className="integrations-org-identity">
            <OrgAvatar
              name={detail?.name}
              id={organizationId}
              image={detail?.image}
              className="integrations-org-logo"
            />
            <div style={{ minWidth: 0 }}>
              <div className="integrations-org-name-row">
                <h2 className="integrations-org-name">
                  {detail?.name || organizationId}
                </h2>
                <StatusBadge state={detail?.effectiveState} />
              </div>
              <div className="integrations-org-id integrations-mono">
                {organizationId}
                <button
                  type="button"
                  className="integrations-copy-id"
                  title="Copy Organization ID"
                  onClick={() => {
                    navigator.clipboard?.writeText(organizationId);
                    toast.success("Organization ID copied to clipboard.");
                  }}
                >
                  ⧉
                </button>
              </div>
            </div>
          </div>
          <div className="integrations-head-actions">
            {loadedAt ? (
              <span
                className="integrations-head-freshness"
                title={formatDatePrecise(loadedAt)}
              >
                Updated {formatRelativeTime(loadedAt)}
              </span>
            ) : null}
            <button
              type="button"
              className={`integrations-head-refresh ${loading ? "is-loading" : ""}`}
              title="Reload this organization"
              disabled={loading}
              onClick={() => {
                loadDetail();
                loadStats();
              }}
            >
              ↻
            </button>
            {onClose ? (
              <button
                type="button"
                className="integrations-head-close"
                title="Close"
                onClick={onClose}
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>

        {hasManyCompanies || canAddCompany ? (
          <div
            className="integrations-tabs-bar"
            role="tablist"
            aria-label="LendingPad companies"
            style={{ marginTop: 15, marginBottom: 0 }}
          >
            {hasManyCompanies || isAddingCompany
              ? companies.map((entry) => (
                  <button
                    key={entry.companyId}
                    type="button"
                    role="tab"
                    aria-selected={
                      !isAddingCompany && entry.companyId === activeCompanyId
                    }
                    className={`integrations-tab-pill ${
                      !isAddingCompany && entry.companyId === activeCompanyId
                        ? "active"
                        : ""
                    }`}
                    title={entry.companyId}
                    disabled={isBusy}
                    onClick={() => selectCompany(entry.companyId)}
                  >
                    {entry.displayName}
                  </button>
                ))
              : null}
            {canAddCompany ? (
              <button
                type="button"
                role="tab"
                aria-selected={isAddingCompany}
                className={`integrations-tab-pill ${
                  isAddingCompany ? "active" : ""
                }`}
                disabled={isBusy}
                onClick={startAddingCompany}
              >
                ＋ Add company
              </button>
            ) : null}
          </div>
        ) : null}

        <div
          className={`integrations-status-banner ${meta.cls} ${
            meta.spin ? "spinning" : ""
          }`}
        >
          <div className="integrations-sb-ico" aria-hidden="true">
            {meta.icon}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="integrations-sb-title">{bannerTitle}</div>
            <div className="integrations-sb-desc">{bannerDesc}</div>
            {hasErrorBlock ? (
              <div className="integrations-error-detail">
                {showTech ? (
                  <div className="integrations-error-tech">
                    {detail.lastError}
                  </div>
                ) : null}
                {recommendation ? (
                  <div className="integrations-error-fix">
                    <span aria-hidden="true">💡</span>
                    <span>
                      <b>What to do:</b> {recommendation}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="integrations-action-bar">
        <div className="integrations-action-grid">
          <button
            type="button"
            className={`integrations-btn integrations-btn-primary ${
              operation === "activate" ||
              operation === "backfill" ||
              (operation === "test" && primary.primaryIsTest)
                ? "is-loading"
                : ""
            }`}
            title={
              (primary.key === "activate" || primary.key === "test") &&
              !hasSavedCredentials
                ? credsTip
                : primary.tip || ""
            }
            disabled={
              isBusy ||
              primary.disabled ||
              ((primary.key === "activate" || primary.key === "test") &&
                !hasSavedCredentials)
            }
            onClick={() => handlePrimary(primary)}
          >
            {primary.icon ? (
              <span className="integrations-btn-ico" aria-hidden="true">
                {primary.icon}
              </span>
            ) : null}
            {primary.label}
          </button>

          {!primary.primaryIsTest ? (
            <button
              type="button"
              className={`integrations-btn integrations-btn-secondary ${
                operation === "test" ? "is-loading" : ""
              }`}
              title={
                hasSavedCredentials
                  ? "Check the connection without changing anything."
                  : credsTip
              }
              disabled={isBusy || !hasSavedCredentials}
              onClick={() =>
                runAction(
                  "test",
                  (body) => testLendingPadConnection(env, body),
                  "Connection successful.",
                )
              }
            >
              <span className="integrations-btn-ico" aria-hidden="true">
                ⇄
              </span>
              Test Connection
            </button>
          ) : null}

          <button
            type="button"
            className="integrations-btn integrations-btn-secondary"
            disabled={isBusy}
            onClick={handleCopySupport}
          >
            <span className="integrations-btn-ico" aria-hidden="true">
              ⧉
            </span>
            Copy Support Request
          </button>

          <button
            type="button"
            className="integrations-btn integrations-btn-danger-outline"
            title={
              !hasSavedCredentials
                ? "Nothing to disable yet."
                : "Turn the integration off for this organization."
            }
            disabled={
              isBusy ||
              detail?.effectiveState === "disabled" ||
              !hasSavedCredentials
            }
            onClick={() => setConfirmAction("disable")}
          >
            <span className="integrations-btn-ico" aria-hidden="true">
              ⊘
            </span>
            Disable
          </button>
        </div>
        {!hasSavedCredentials ? (
          <div className="integrations-action-hint">
            <span aria-hidden="true">💡</span>
            <span>
              Add the Company ID and Admin User ID in the{" "}
              <b>Connection</b> tab, then Save to enable testing and
              activation.
            </span>
          </div>
        ) : null}
      </div>

      {/* Setup tabs are the three decisions, in the order they apply; Activity
          tabs only observe. Grouping them is what tells an operator which is
          which without opening any of them. */}
      <div className="integrations-tabs" role="tablist">
        {[
          { tabs: [{ id: "overview", label: "Overview" }] },
          {
            label: "Setup",
            tabs: [
              { id: "connection", label: "Connection" },
              { id: "policy", label: "Import policy" },
              { id: "people", label: "People" },
            ],
          },
          {
            label: "Activity",
            tabs: [
              { id: "activity", label: "History" },
              // Real totals, not the size of the page we happened to fetch.
              { id: "syncs", label: "Syncs", count: stats?.syncHistory?.total },
              { id: "reconcile", label: "Data Check" },
            ],
          },
        ].map((group, index) => (
          <div key={group.label || index} className="integrations-tab-group">
            <div className="integrations-tab-group-label">{group.label || "\u00a0"}</div>
            <div className="integrations-tab-group-tabs">
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`integrations-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => selectTab(tab.id)}
                >
                  {tab.label}
                  {typeof tab.count === "number" ? (
                    <span className="integrations-tab-count">{tab.count}</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {activeTab === "overview" ? (
        <div className="integrations-tab-panel">
          {/* The connection test only proves LendingPad answers when we call
              it. This answers the different, more useful question: is data
              still arriving on its own? */}
          <div className="integrations-section-title">
            Data feed
            <span>{feed.label}</span>
          </div>
          <div className={`integrations-feed-card ${feed.cls}`}>
            <div className="integrations-feed-ico" aria-hidden="true">
              {feed.icon}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="integrations-feed-title">
                {detail?.lastDeliveryAt
                  ? `Last delivery ${formatRelativeTime(detail.lastDeliveryAt)}`
                  : "No delivery recorded"}
              </div>
              <div className="integrations-feed-desc">{feed.desc}</div>
              {detail?.feedNeverDelivered ? (
                <div className="integrations-error-fix">
                  <span aria-hidden="true">💡</span>
                  <span>
                    <b>What to do:</b> This organization has been live since{" "}
                    {formatDate(detail?.dateApproved)} and LendingPad has never
                    delivered an export for it. The connection works, so the
                    exports were most likely never switched on for this Company
                    ID. Copy the support request and ask LendingPad to confirm.
                  </span>
                </div>
              ) : detail?.feedSilent ? (
                <div className="integrations-error-fix">
                  <span aria-hidden="true">💡</span>
                  <span>
                    <b>What to do:</b> No updates from LendingPad since{" "}
                    {formatRelativeTime(detail?.lastDeliveryAt)}. This may just be
                    a quiet period. Open the <b>Data Check</b> tab to see whether
                    anything is actually missing before treating it as a problem.
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="integrations-section-title" style={{ marginTop: 16 }}>
            Health checks
            <span>
              {passedChecks} of {totalChecks} passing
            </span>
          </div>
          <HealthChecks checks={detail?.readinessChecks || []} />

          <div className="integrations-facts">
            <div className="integrations-fact">
              <div className="integrations-fact-label">Last checked</div>
              <div className="integrations-fact-value">{lastTestSummary}</div>
            </div>
            {/* Two clocks, deliberately. A delivery arriving proves the feed is
                alive; an import only happens when the loan passes the stage
                filter, so the two move independently. */}
            <div className="integrations-fact">
              <div className="integrations-fact-label">
                Last delivery received
                <InfoDot
                  summary="The last time LendingPad told Addy something changed."
                  detail="The integration's heartbeat. If it stops, LendingPad changes stop reaching Addy, even while the connection test still passes."
                />
              </div>
              <div
                className="integrations-fact-value"
                title={formatDatePrecise(detail?.lastDeliveryAt)}
              >
                {detail?.lastDeliveryAt
                  ? formatRelativeTime(detail.lastDeliveryAt)
                  : "Never"}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">
                Last loan imported
                <InfoDot
                  summary="The last time a loan was written into Addy."
                  detail="Different from the delivery clock: deliveries for stages this org doesn't import are skipped, so this can lag a live feed. A recent delivery with an old import is fine; the reverse isn't."
                />
              </div>
              <div
                className="integrations-fact-value"
                title={formatDatePrecise(detail?.lastSyncedAt)}
              >
                {formatRelativeTime(detail?.lastSyncedAt)}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">Loans in Addy</div>
              <div className="integrations-fact-value">
                {stats?.loans?.fromLendingPad ?? "—"}
                {stats?.loans?.total != null &&
                stats?.loans?.total !== stats?.loans?.fromLendingPad ? (
                  <div className="integrations-fact-sub">
                    {stats.loans.total} total from all sources
                    {stats.companyId ? " in the organization" : ""}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">
                {stats?.companyId ? "Documents in the organization" : "Documents"}
              </div>
              <div className="integrations-fact-value">
                {stats?.documents?.total ?? "—"}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">Sync history</div>
              <div className="integrations-fact-value">
                {stats?.syncHistory?.total ?? "—"}
                {stats?.syncHistory?.byTrigger ? (
                  <div className="integrations-fact-sub">
                    {stats.syncHistory.byTrigger.webhook ?? 0} deliveries ·{" "}
                    {stats.syncHistory.byTrigger.worker ?? 0} imports
                  </div>
                ) : null}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">Activation attempts</div>
              <div className="integrations-fact-value">
                {detail?.activationAttempts ?? 0}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">Config version</div>
              <div className="integrations-fact-value">
                {detail?.configVersion ?? "—"}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">Requested by</div>
              <div className="integrations-fact-value">
                {detail?.requestedBy?.name ||
                  detail?.requestedBy?.email ||
                  "—"}
                {detail?.requestedBy?.name && detail?.requestedBy?.email ? (
                  <div className="integrations-fact-sub">
                    {detail.requestedBy.email}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="integrations-fact">
              <div className="integrations-fact-label">Requested</div>
              <div className="integrations-fact-value">
                {formatDate(detail?.dateRequested)}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "connection" ? (
        <div className="integrations-tab-panel">
          {isAddingCompany ? (
            <CompanyScopeNote>
              A new LendingPad company for {detail?.name || "this organization"}.
              It starts with the most restrictive import rules: the
              organization&rsquo;s team only, default stages, no selected loans,
              no extra addresses and no ignored folders.
            </CompanyScopeNote>
          ) : hasManyCompanies ? (
            <CompanyScopeNote>
              Testing, activating, syncing or disabling acts on{" "}
              <b>{companyName}</b> only.
            </CompanyScopeNote>
          ) : null}
          <div className="integrations-field">
            <label className="integrations-field-label">Company ID</label>
            <input
              className="integrations-input integrations-mono-input"
              value={company}
              placeholder="Paste the LendingPad Company ID"
              disabled={isCompanyIdLocked}
              onChange={(event) => {
                hasUnsavedCredentialsRef.current = true;
                setCompany(event.target.value);
              }}
            />
          </div>
          {isCompanyIdLocked ? (
            <p className="integrations-field-hint">
              Locked while this company is active. LendingPad sends its loans
              under this ID.
            </p>
          ) : null}
          <div className="integrations-field">
            <label className="integrations-field-label">Admin User ID</label>
            <input
              className="integrations-input integrations-mono-input"
              value={user}
              placeholder="Paste the LendingPad Admin User ID"
              onChange={(event) => {
                hasUnsavedCredentialsRef.current = true;
                setUser(event.target.value);
              }}
            />
          </div>
          {activeCompanyId || isAddingCompany ? (
            <div className="integrations-field">
              <label className="integrations-field-label">Alias (optional)</label>
              <input
                className="integrations-input"
                value={alias}
                placeholder="How ops and the customer name this company"
                onChange={(event) => {
                  hasUnsavedCredentialsRef.current = true;
                  setAlias(event.target.value);
                }}
              />
            </div>
          ) : null}
          <div className="integrations-config-save-row">
            {isAddingCompany ? (
              <button
                type="button"
                className="integrations-btn integrations-btn-ghost"
                disabled={isBusy}
                onClick={stopAddingCompany}
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              className={`integrations-btn integrations-btn-ghost ${
                operation === "save" ? "is-loading" : ""
              }`}
              title="Saves these IDs only. Does not activate or sync."
              disabled={isBusy || (isAddingCompany && !company.trim())}
              onClick={isAddingCompany ? handleAddCompany : handleSave}
            >
              Save
            </button>
          </div>
          <div className="integrations-save-note">
            <span aria-hidden="true">ℹ️</span>
            <span>
              Saving only stores these IDs. It won't activate the integration,
              start a sync, or notify the customer. Run <b>Test Connection</b>{" "}
              afterwards.
            </span>
          </div>
        </div>
      ) : null}

      {activeTab === "policy" ? (
        <div className="integrations-tab-panel">
          {hasManyCompanies ? (
            <CompanyScopeNote>
              These rules apply to <b>{companyName}</b> only. Each LendingPad
              company keeps its own.
            </CompanyScopeNote>
          ) : null}
          <LendingPadImportPolicyTab
            syncStages={detail?.syncStages}
            importScope={detail?.importScope}
            loansInAddy={stats?.loans?.fromLendingPad ?? null}
            disabled={isBusy}
            savingScope={operation === "importScope"}
            savingStages={operation === "stages"}
            onSaveScope={handleSaveImportScope}
            onSaveStages={handleSaveStages}
            onOpenPeople={() => selectTab("people")}
            ignoredDocumentFolders={detail?.ignoredDocumentFolders}
            savingFolders={operation === "folders"}
            onSaveFolders={handleSaveIgnoredFolders}
            selectedLoans={detail?.selectedLoans}
            savingSelectedLoans={operation === "selectedLoans"}
            onAddSelectedLoan={handleAddSelectedLoan}
            onRemoveSelectedLoan={handleRemoveSelectedLoan}
          />
        </div>
      ) : null}

      {activeTab === "people" ? (
        <div className="integrations-tab-panel">
          {hasManyCompanies ? (
            <CompanyScopeNote>
              Declared LendingPad addresses count for <b>{companyName}</b> only.
              Linked addresses belong to the person, in every company.
            </CompanyScopeNote>
          ) : null}
          <LendingPadPeopleTab
            people={people}
            loading={peopleLoading}
            error={peopleError}
            busyKey={
              typeof operation === "string" &&
              (operation.startsWith("visibility:") || operation.startsWith("declared:"))
                ? operation.replace(/^visibility:/, "")
                : null
            }
            disabled={isBusy}
            onSelect={handleRoleSelect}
            onClear={handleRoleClear}
            onAddAddress={handleAddAddress}
            onRemoveAddress={handleRemoveAddress}
            onLinkLosEmail={handleLinkLosEmail}
            onUnlinkLosEmail={handleUnlinkLosEmail}
            onRetry={loadPeople}
            onExplain={explainAccess}
          />
        </div>
      ) : null}

      {activeTab === "activity" ? (
        <div className="integrations-tab-panel">
          <ActivityTimeline events={activity} />
        </div>
      ) : null}

      {activeTab === "syncs" ? (
        <div className="integrations-tab-panel">
          {detail?.canRetryBackfill || isSyncing ? (
            <div className="integrations-sync-run">
              <div className="integrations-sync-run-head">
                <div className="integrations-sync-run-title">
                  {isSyncing ? "Loan sync in progress" : "Manual loan sync"}
                </div>
                {isSyncing ? (
                  <StatusBadge state="syncing" />
                ) : (
                  <button
                    type="button"
                    className="integrations-btn integrations-btn-secondary integrations-btn-small"
                    disabled={isBusy}
                    onClick={() => setConfirmAction("backfill")}
                  >
                    <span className="integrations-btn-ico" aria-hidden="true">
                      ⇅
                    </span>
                    Sync Data
                  </button>
                )}
              </div>
              <div className="integrations-sync-run-meta">
                {isSyncing
                  ? `In progress${backfillState ? ` · ${backfillState}` : ""}`
                  : "Import the latest loans from LendingPad on demand."}
              </div>
            </div>
          ) : (
            <div className="integrations-sync-run">
              <div className="integrations-sync-run-head">
                <div className="integrations-sync-run-title">
                  Manual sync unavailable
                </div>
                <StatusBadge state="pending" />
              </div>
              <div className="integrations-sync-run-meta">
                Activate the integration with a passing connection test before
                syncing loans.
              </div>
            </div>
          )}

          <div className="integrations-section-title" style={{ marginTop: 16 }}>
            Sync history
            <span>
              {stats?.syncHistory?.total != null
                ? `${stats.syncHistory.total} total`
                : `${syncHistory.length} shown`}
            </span>
          </div>

          {/* Anchors the table to the organization it belongs to. Without it,
              switching orgs leaves a table of loan ids with nothing tying them
              to anyone — easy to read as the wrong org's data. */}
          <div className="integrations-sync-scope">
            {detail?.name || organizationId}
            <span className="integrations-mono">{organizationId}</span>
          </div>

          <div className="integrations-filter-row integrations-sync-filters">
            <select
              className="integrations-select"
              value={syncFilters.triggeredBy}
              onChange={(event) =>
                applySyncFilter({ triggeredBy: event.target.value })
              }
            >
              <option value="">All origins</option>
              <option value="webhook">Deliveries from LendingPad</option>
              <option value="worker">Imports by Addy</option>
            </select>
            <select
              className="integrations-select"
              value={syncFilters.status}
              onChange={(event) => applySyncFilter({ status: event.target.value })}
            >
              <option value="">All results</option>
              <option value="completed">Completed</option>
              <option value="skipped">Skipped</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {syncHistory.length === 0 ? (
            <div className="integrations-empty">
              {syncFilters.triggeredBy || syncFilters.status
                ? "No sync history matches these filters."
                : "No loans have been synced yet."}
            </div>
          ) : (
            <>
              <div className="integrations-table-scroll">
                <table className="integrations-sync-table">
                  <thead>
                    <tr>
                      <th>Loan</th>
                      {/* The column that tells a live integration from a dead
                          one: deliveries mean LendingPad is pushing to us. */}
                      <th>
                        Origin
                        <InfoDot
                          summary="What caused this row."
                          detail="Delivery = LendingPad announced a change. Import = Addy fetched and wrote the loan. Healthy orgs show both, paired; imports alone mean it's kept current by hand."
                        />
                      </th>
                      <th>Result</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncHistory.map((entry) => {
                      const origin = originMeta(entry);
                      const completedMillis = Date.parse(entry.completedAt);

                      return (
                        <tr key={entry.id}>
                          <td className="integrations-mono">
                            {entry.loanId || entry.loanReferenceId || "—"}
                          </td>
                          <td>
                            <InfoTip
                              summary={origin.summary}
                              detail={origin.detail}
                            >
                              <span
                                className={`integrations-trigger-chip ${origin.cls}`}
                              >
                                {origin.label}
                              </span>
                            </InfoTip>
                          </td>
                          <td>
                            {entry.status || entry.result || "—"}
                            {/* Previously dropped: a skipped row without its
                                reason reads as an error when it is usually the
                                stage policy working correctly. */}
                            {entry.reason ? (
                              <div className="integrations-sync-reason">
                                {describeSyncReason(
                                  entry.reason,
                                  detail?.syncStages?.availableStages,
                                )}
                              </div>
                            ) : null}
                          </td>
                          <td title={formatDatePrecise(completedMillis)}>
                            {formatDate(completedMillis)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {syncCursor ? (
                <div className="integrations-config-save-row">
                  <button
                    type="button"
                    className={`integrations-btn integrations-btn-ghost integrations-btn-small ${
                      loadingMoreSyncs ? "is-loading" : ""
                    }`}
                    disabled={loadingMoreSyncs}
                    onClick={loadMoreSyncs}
                  >
                    Load more
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {activeTab === "reconcile" ? (
        <div className="integrations-tab-panel">
          <div className="integrations-sync-run">
            <div className="integrations-sync-run-head">
              <div className="integrations-sync-run-title">
                Data Check
              </div>
              <button
                type="button"
                className={`integrations-btn integrations-btn-secondary integrations-btn-small ${
                  operation === "reconcile" ? "is-loading" : ""
                }`}
                disabled={isBusy || !hasSavedCredentials}
                onClick={handleReconcile}
              >
                <span className="integrations-btn-ico" aria-hidden="true">
                  ⇄
                </span>
                {operation === "reconcile" ? "Checking…" : "Check for missing data"}
              </button>
            </div>
            <div className="integrations-sync-run-meta">
              Asks LendingPad what it has right now and compares it to what Addy
              imported, so you can see whether this organization is actually
              behind. It lists any loans that changed in LendingPad but never
              reached us. Read-only: it checks, it never imports or changes
              anything. Takes up to a minute on a large organization.
            </div>
          </div>

          {reconcileError ? (
            <div className="integrations-result err">{reconcileError}</div>
          ) : null}

          {reconciliation ? (
            <>
              <div className="integrations-facts" style={{ marginTop: 16 }}>
                <div className="integrations-fact">
                  <div className="integrations-fact-label">In LendingPad</div>
                  <div className="integrations-fact-value">
                    {reconciliation.counts.inLendingPad}
                  </div>
                </div>
                <div className="integrations-fact">
                  <div className="integrations-fact-label">In Addy</div>
                  <div className="integrations-fact-value">
                    {reconciliation.counts.inAddy}
                  </div>
                </div>
                <div className="integrations-fact">
                  <div className="integrations-fact-label">Behind</div>
                  <div className="integrations-fact-value">
                    {reconciliation.counts.changedSince}
                    <div className="integrations-fact-sub">
                      changed in LendingPad since we imported
                    </div>
                  </div>
                </div>
                <div className="integrations-fact">
                  <div className="integrations-fact-label">Missing</div>
                  <div className="integrations-fact-value">
                    {reconciliation.counts.missingInAddy}
                    <div className="integrations-fact-sub">never received</div>
                  </div>
                </div>
              </div>

              {reconciliation.inSync ? (
                <div className="integrations-result ok" style={{ marginTop: 12 }}>
                  Addy matches LendingPad. Nothing is missing or behind.
                </div>
              ) : (
                <>
                  <div
                    className="integrations-section-title"
                    style={{ marginTop: 16 }}
                  >
                    Not in sync
                    <span>
                      checked {reconciliation.stagesChecked} stages in{" "}
                      {Math.round(reconciliation.durationMs / 1000)}s
                    </span>
                  </div>
                  <div className="integrations-table-scroll">
                    <table className="integrations-sync-table">
                      <thead>
                        <tr>
                          <th>Loan</th>
                          <th>Stage</th>
                          <th>Changed in LendingPad</th>
                          <th>In Addy</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconciliation.missingInAddy.map((loan) => (
                          <tr key={`missing-${loan.losId}`}>
                            <td className="integrations-mono">
                              {loan.loanNumber || loan.losId}
                            </td>
                            <td>{loan.statusName || loan.statusId}</td>
                            <td title={formatDatePrecise(loan.lastModifiedAt)}>
                              {formatDate(loan.lastModifiedAt)}
                            </td>
                            <td>
                              <span className="integrations-mini-badge error">
                                Never received
                              </span>
                            </td>
                          </tr>
                        ))}
                        {reconciliation.changedSince.map((loan) => (
                          <tr key={`changed-${loan.losId}`}>
                            <td className="integrations-mono">
                              {loan.loanNumber || loan.losId}
                            </td>
                            <td>{loan.statusName || loan.statusId}</td>
                            <td title={formatDatePrecise(loan.lastModifiedAt)}>
                              {formatDate(loan.lastModifiedAt)}
                            </td>
                            <td title={formatDatePrecise(loan.addyUpdatedAt)}>
                              {formatRelativeTime(loan.addyUpdatedAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {reconciliation.counts.notReturned > 0 ? (
                    <div className="integrations-save-note">
                      <span aria-hidden="true">ℹ️</span>
                      <span>
                        {reconciliation.counts.notReturned} loan(s) in Addy were
                        not returned by this walk. That is expected when a loan
                        moves into a stage this organization does not import.
                      </span>
                    </div>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </div>
      ) : null}

      <ConfirmationDialog
        open={confirmAction?.kind === "visibility"}
        title="Let them see every loan"
        message={
          confirmAction?.kind === "visibility"
            ? `${confirmAction.name} will see every loan in this team, instead of only their own. Their Addy role still applies.`
            : ""
        }
        confirmLabel="Let them see all loans"
        isLoading={Boolean(operation)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          applyRoleSelection(
            confirmAction.subject,
            confirmAction.opsRoleSelection,
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction?.kind === "add-address"}
        title="Add a LendingPad-only person"
        message={
          confirmAction?.kind === "add-address"
            ? `${confirmAction.email} will count as one of this organization's people. Their existing loans in the imported stages are imported now, and new ones keep syncing. They cannot sign in to Addy; this changes what the team sees, not what they see.`
            : ""
        }
        confirmLabel="Add and import their loans"
        isLoading={Boolean(operation)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          applyDeclaredAddresses(
            confirmAction.email,
            [...declaredEmails(), confirmAction.email],
            `Added ${confirmAction.email}.`,
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction?.kind === "remove-address"}
        title="Remove this person"
        message={
          confirmAction?.kind === "remove-address"
            ? `${confirmAction.email} will stop counting as one of this organization's people. Their loans stop updating; nothing already imported is deleted.`
            : ""
        }
        confirmLabel="Remove"
        isDestructive
        isLoading={Boolean(operation)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          const remaining = declaredEmails().filter(
            (email) => email !== confirmAction.email,
          );

          return applyDeclaredAddresses(
            confirmAction.email,
            remaining.length > 0 ? remaining : null,
            `Removed ${confirmAction.email}.`,
          );
        }}
      />

      <ConfirmationDialog
        open={confirmAction?.kind === "link-los-email"}
        title="Link this address"
        message={
          confirmAction?.kind === "link-los-email"
            ? `${confirmAction.email} will count as ${confirmAction.name}. Every loan carrying that address opens for them from their next page load, in every organization they belong to. An address can belong to one Addy user at a time.`
            : ""
        }
        confirmLabel="Link"
        isLoading={Boolean(operation)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          applyLosEmailChange(
            confirmAction.subject,
            confirmAction.email,
            linkLendingPadLosEmail,
            `Linked ${confirmAction.email} to ${confirmAction.name}.`,
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction?.kind === "unlink-los-email"}
        title="Unlink this address"
        message={
          confirmAction?.kind === "unlink-los-email"
            ? `${confirmAction.email} will no longer count as ${confirmAction.name}. Loans that carry only that address disappear from their board. If the address is declared below, it stays declared and its loans keep importing.`
            : ""
        }
        confirmLabel="Unlink"
        isDestructive
        isLoading={Boolean(operation)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          applyLosEmailChange(
            confirmAction.subject,
            confirmAction.email,
            unlinkLendingPadLosEmail,
            `Unlinked ${confirmAction.email}.`,
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction?.kind === "import-scope"}
        title="Import every loan in this company"
        message="Every loan LendingPad delivers for this Company ID, in the selected stages, will be imported and made visible to this organization, including loans belonging to originators who are not Addy users of it. Switching back later stops updates but keeps the loans already imported."
        confirmLabel="Import every loan"
        isLoading={operation === "importScope"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => applyImportScope(confirmAction.importScope)}
      />

      <ConfirmationDialog
        open={confirmAction?.kind === "clear-capture"}
        title="Forget these roles"
        message={
          confirmAction?.kind === "clear-capture"
            ? `LendingPad will stop limiting ${confirmAction.name} until Addy reads their roles again, which happens the next time they open LendingPad. Usually that is the same day. Use this to unblock someone now, or to clear roles left by a previous owner.`
            : ""
        }
        confirmLabel="Forget them"
        isDestructive
        isLoading={Boolean(operation)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          runAction(
            `visibility:${subjectKey(confirmAction.subject)}`,
            (body) =>
              clearLendingPadRoleCapture(env, {
                ...body,
                subjectType: confirmAction.subject.subjectType,
                memberId: confirmAction.subject.memberId || null,
              }),
            "Forgotten. Addy will read their roles again on their next LendingPad visit.",
            { after: loadPeople },
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction === "activate"}
        title="Activate LendingPad"
        message="This runs the connection checks and, if they pass, takes the integration live for this organization."
        confirmLabel="Activate"
        isLoading={operation === "activate"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          runAction(
            "activate",
            (body) =>
              detail?.canActivate
                ? activateLendingPad(env, body)
                : retryLendingPadActivation(env, body),
            "Activation completed. The integration is now live.",
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction === "disable"}
        title="Disable LendingPad"
        message="Loan syncing will stop for this organization until you re-activate it. Configuration and history are kept."
        confirmLabel="Disable integration"
        isDestructive
        isLoading={operation === "disable"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          runAction(
            "disable",
            (body) => disableLendingPad(env, body),
            "Integration disabled. An audit event was recorded.",
          )
        }
      />

      <ConfirmationDialog
        open={confirmAction === "backfill"}
        title="Sync Data"
        message="This imports the latest loans from LendingPad for this organization. It's safe to run and may take a few minutes."
        confirmLabel="Start sync"
        isLoading={operation === "backfill"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          runAction(
            "backfill",
            (body) => retryLendingPadBackfill(env, body),
            "Sync started. Loans are being imported from LendingPad.",
          )
        }
      />
    </div>
  );
}
